import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Refresh,
  UploadFile,
  Delete,
  Download,
  Description,
  Visibility,
  Engineering,
  Assignment,
  Search,
  BuildCircle,
  CheckCircleOutline,
  HourglassTop,
  Add
} from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';
import {
  HANGAR_JOB_TYPE_OPTIONS,
  HANGAR_ENVIRONMENT_OPTIONS
} from '../constants/maintenanceWorkOrderTypes';
import {
  viewAuthenticatedPdf,
  downloadAuthenticatedPdf,
  openAuthenticatedPdfViaToken
} from '../utils/authenticatedPdf';
import {
  COLORS,
  PAGE_BACKGROUND,
  PAGE_AMBIENT_SX,
  panelShellSx,
  glassCardSx,
  glassCardHoverSx,
  surfaceInsetSx,
  sectionTitleSx,
  pillPrimarySx,
  pillSecondarySx,
  alertErrorSx,
  alertInfoSx
} from './clientPortal/clientPortalTheme';

const STAGE_ORDER = [
  'AWAITING_PLANNING',
  'PLANNED',
  'STORE',
  'IN_MRO',
  'MRO_QM',
  'COMPLETED'
];

const STAGE_META = {
  AWAITING_PLANNING: { label: 'Request', color: COLORS.indigo, bg: COLORS.indigoGlass },
  PLANNED: { label: 'WO created', color: COLORS.orange, bg: COLORS.orangeGlass },
  STORE: { label: 'Store', color: '#b8860b', bg: 'rgba(184, 134, 11, 0.12)' },
  IN_MRO: { label: 'In MRO', color: COLORS.amber, bg: COLORS.amberGlass },
  MRO_QM: { label: 'QM review', color: COLORS.orangeLight, bg: COLORS.orangeGlass },
  COMPLETED: { label: 'Completed', color: COLORS.green, bg: COLORS.greenGlass },
  CANCELLED: { label: 'Cancelled', color: COLORS.grey, bg: COLORS.greyGlass },
  REJECTED: { label: 'Rejected', color: COLORS.red, bg: COLORS.redGlass },
  UNKNOWN: { label: 'Unknown', color: COLORS.greyMuted, bg: COLORS.greyGlass }
};

const POLL_MS = 12000;

const emptyForm = () => ({
  registration: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  title: '',
  type: 'MAINTENANCE',
  maintenanceType: 'LINE_INSPECTION',
  priority: 'MEDIUM',
  description: '',
  scheduledDate: '',
  notes: '',
  pendingFiles: []
});

function stageActiveIndex(stage) {
  if (stage === 'CANCELLED' || stage === 'REJECTED') return -1;
  const idx = STAGE_ORDER.indexOf(stage);
  return idx >= 0 ? idx : 0;
}

function formatActionAt(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatShortDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

async function downloadAuthenticatedBinary(url, filename) {
  const response = await axios.get(url, {
    responseType: 'blob',
    skipGlobalErrorToast: true,
    headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
  });
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data]);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

const EDR_VIEW_FIELDS = [
  ['edrReferenceNumber', 'EDR reference'],
  ['formNumber', 'Form number'],
  ['referenceDate', 'Reference date'],
  ['aircraftType', 'Aircraft type'],
  ['aircraftRegistration', 'Registration'],
  ['aircraftHours', 'Aircraft hours'],
  ['aircraftLandings', 'Landings'],
  ['icplNumber', 'Authorisation number'],
  ['defectDescription', 'Defect description'],
  ['actionTaken', 'Action taken'],
  ['snagReportedBy', 'Snag reported by'],
  ['raisingPersonName', 'Raising person'],
  ['designation', 'Designation']
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: COLORS.field,
    borderRadius: 2.5,
    '& fieldset': { borderColor: COLORS.fieldBorder },
    '&:hover fieldset': { borderColor: COLORS.fieldBorderStrong },
    '&.Mui-focused fieldset': { borderColor: COLORS.orange }
  },
  '& .MuiInputLabel-root': { color: COLORS.textSecondary },
  '& .MuiInputBase-input': { color: COLORS.text }
};

const StageChip = ({ stage, label }) => {
  const meta = STAGE_META[stage] || STAGE_META.UNKNOWN;
  return (
    <Chip
      size="small"
      label={label || meta.label}
      sx={{
        fontWeight: 700,
        borderRadius: 999,
        bgcolor: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}55`
      }}
    />
  );
};

const KpiPill = ({ title, value, accent, bg, icon }) => (
  <Box
    sx={{
      height: '100%',
      borderRadius: 999,
      px: 2,
      py: 1.15,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.25,
      bgcolor: bg,
      background: `linear-gradient(135deg, ${bg} 0%, rgba(255,255,255,0.85) 100%)`,
      border: `1.5px solid ${accent}70`,
      boxShadow: `0 4px 16px ${accent}18, inset 0 1px 0 rgba(255,255,255,0.8)`,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)'
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      {icon}
      <Typography
        variant="caption"
        sx={{
          color: accent,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          fontSize: '0.68rem'
        }}
        noWrap
      >
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" fontWeight={800} sx={{ color: accent, lineHeight: 1 }}>
      {value}
    </Typography>
  </Box>
);

const FormRow = ({ label, children, hint }) => (
  <Box
    sx={{
      ...surfaceInsetSx,
      p: 1.5,
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { sm: 'center' },
      gap: 1.25
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.text }}>
        {label}
      </Typography>
      {hint ? (
        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {children}
    </Stack>
  </Box>
);

const OperatorPortal = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveConnected, setLiveConnected] = useState(false);
  const [listError, setListError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [detailJob, setDetailJob] = useState(null);
  const [edrView, setEdrView] = useState(null);
  const [formBusy, setFormBusy] = useState('');
  const [detailError, setDetailError] = useState('');
  const [historyTab, setHistoryTab] = useState('active');
  const [search, setSearch] = useState('');
  const [showSubmit, setShowSubmit] = useState(true);
  const detailJobIdRef = useRef(null);
  const fetchInFlightRef = useRef(false);

  useEffect(() => {
    detailJobIdRef.current = detailJob?._id ? String(detailJob._id) : null;
  }, [detailJob]);

  const fetchJobs = useCallback(async ({ quiet = false } = {}) => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    if (!quiet) {
      setLoading(true);
      setListError('');
    }
    try {
      const res = await axios.get('/api/operator-portal/jobs');
      const nextJobs = Array.isArray(res.data?.jobs) ? res.data.jobs : [];
      setJobs(nextJobs);
      const openId = detailJobIdRef.current;
      if (openId) {
        const refreshed = nextJobs.find((j) => String(j._id) === openId);
        if (refreshed) setDetailJob(refreshed);
      }
    } catch (err) {
      if (!quiet) {
        setListError(err.response?.data?.message || 'Failed to load jobs');
        setJobs([]);
      }
    } finally {
      fetchInFlightRef.current = false;
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchJobs({ quiet: true });
      }
    }, POLL_MS);
    const onFocus = () => fetchJobs({ quiet: true });
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchJobs({ quiet: true });
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchJobs]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = io(
      process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : undefined,
      {
        path: '/socket.io',
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 2000
      }
    );

    const refresh = () => fetchJobs({ quiet: true });
    socket.on('connect', () => setLiveConnected(true));
    socket.on('disconnect', () => setLiveConnected(false));
    socket.on('connect_error', () => setLiveConnected(false));
    socket.on('operator-portal:job-updated', refresh);

    return () => {
      socket.off('operator-portal:job-updated', refresh);
      socket.disconnect();
      setLiveConnected(false);
    };
  }, [fetchJobs]);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const addPendingFiles = (fileList) => {
    const next = Array.from(fileList || []);
    if (!next.length) return;
    setForm((prev) => ({
      ...prev,
      pendingFiles: [...(prev.pendingFiles || []), ...next]
    }));
  };

  const removePendingFile = (index) => {
    setForm((prev) => ({
      ...prev,
      pendingFiles: (prev.pendingFiles || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    try {
      if (!form.registration.trim()) {
        setSubmitError('Aircraft registration is required');
        setSubmitting(false);
        return;
      }
      if (!form.title.trim()) {
        setSubmitError('Job title is required');
        setSubmitting(false);
        return;
      }

      const payload = {
        registration: form.registration.trim(),
        manufacturer: form.manufacturer.trim() || undefined,
        model: form.model.trim() || undefined,
        serialNumber: form.serialNumber.trim() || undefined,
        title: form.title.trim(),
        type: form.type,
        maintenanceType: form.maintenanceType || undefined,
        priority: form.priority,
        description: form.description.trim() || undefined,
        scheduledDate: form.scheduledDate || undefined,
        notes: form.notes.trim() || undefined
      };

      const res = await axios.post('/api/operator-portal/jobs', payload);
      const jobId = res.data?.job?._id;
      const pending = form.pendingFiles || [];
      if (jobId && pending.length) {
        for (const file of pending) {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(`/api/operator-portal/jobs/${jobId}/attachments`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      setSubmitSuccess(
        res.data?.created
          ? `Request submitted (${res.data.job?.externalJobId || 'ok'}). Planning Manager will create the work order.`
          : 'Request already existed for this reference; showing current status.'
      );
      setForm(emptyForm());
      setHistoryTab('active');
      await fetchJobs();
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to submit job');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = useMemo(() => {
    const counts = {
      total: jobs.length,
      active: 0,
      awaiting: 0,
      inMro: 0,
      completed: 0
    };
    jobs.forEach((job) => {
      const stage = job.customerStage;
      if (stage === 'COMPLETED') counts.completed += 1;
      else if (stage !== 'CANCELLED' && stage !== 'REJECTED') {
        counts.active += 1;
        if (stage === 'AWAITING_PLANNING') counts.awaiting += 1;
        if (stage === 'IN_MRO' || stage === 'MRO_QM') counts.inMro += 1;
      }
    });
    return counts;
  }, [jobs]);

  const sortedJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...jobs]
      .filter((job) => {
        const stage = job.customerStage;
        if (historyTab === 'active') {
          if (stage === 'COMPLETED' || stage === 'CANCELLED' || stage === 'REJECTED') return false;
        } else if (historyTab === 'completed') {
          if (stage !== 'COMPLETED') return false;
        }
        if (!q) return true;
        const registration = typeof job.aircraftId === 'object'
          ? job.aircraftId?.registration
          : '';
        const hay = [
          job.mroJobNumber,
          job.externalJobId,
          job.title,
          registration,
          job.customerStageLabel,
          job.customerStage
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [jobs, historyTab, search]);

  const openJobDetails = async (job) => {
    setDetailJob(job);
    setDetailError('');
    try {
      const res = await axios.get(`/api/operator-portal/jobs/${job._id}`);
      if (res.data?.job) setDetailJob(res.data.job);
    } catch (_) {
      // Keep list snapshot if detail fetch fails.
    }
  };

  const runFormAction = async (key, fn) => {
    setFormBusy(key);
    setDetailError('');
    try {
      await fn();
    } catch (err) {
      setDetailError(err.response?.data?.message || err.message || 'Failed to open document');
    } finally {
      setFormBusy('');
    }
  };

  const openPdf = (url) => runFormAction(url, async () => {
    try {
      await viewAuthenticatedPdf(url);
    } catch (err) {
      try {
        openAuthenticatedPdfViaToken(url);
      } catch (fallbackErr) {
        throw fallbackErr.message ? fallbackErr : err;
      }
    }
  });

  const downloadPdf = (url, filename) => runFormAction(`${url}:dl`, async () => {
    await downloadAuthenticatedPdf(`${url}${url.includes('?') ? '&' : '?'}download=1`, filename);
  });

  const jobBase = detailJob?._id ? `/api/operator-portal/jobs/${detailJob._id}` : null;
  const forms = detailJob?.forms || {};

  const formBitsFor = (job) => {
    const jobForms = job.forms || {};
    const bits = [];
    if (jobForms.crsDocument?.available || jobForms.crsPdfAvailable) bits.push('CRS');
    if (jobForms.actionTakenFormAvailable) bits.push('ATF');
    if ((jobForms.edrs || []).length) bits.push(`EDR×${jobForms.edrs.length}`);
    if (jobForms.workPackagePdfAvailable) bits.push('Package');
    return bits;
  };

  return (
    <Box
      sx={{
        background: PAGE_BACKGROUND,
        minHeight: 'calc(100vh - 64px)',
        mx: -3,
        mt: -3,
        mb: -3,
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
        position: 'relative',
        overflow: 'hidden',
        ...PAGE_AMBIENT_SX
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            ...panelShellSx,
            p: { xs: 2.5, md: 3 },
            mb: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Engineering
            sx={{
              position: 'absolute',
              right: -20,
              top: -28,
              fontSize: 160,
              opacity: 0.06,
              color: COLORS.orange,
              transform: 'rotate(-12deg)'
            }}
          />
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            spacing={2}
          >
            <Box sx={{ ...sectionTitleSx, alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: COLORS.orange, fontWeight: 800, letterSpacing: 1.2 }}
                >
                  Hangar client
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ color: COLORS.text, mt: 0.25 }}>
                  Operator Portal
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.75, maxWidth: 520 }}>
                  Submit a work order request. The hangar Planning Manager creates the formal work order,
                  then Store / MRO / QM progress — with CRS, EDR, and release records kept for every job.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={liveConnected ? 'Live updates on' : 'Auto-refreshing'}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 700,
                      bgcolor: liveConnected ? COLORS.greenGlass : COLORS.greyGlass,
                      color: liveConnected ? COLORS.green : COLORS.greyDark,
                      border: `1px solid ${liveConnected ? COLORS.green : COLORS.grey}55`
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${stats.total} job${stats.total === 1 ? '' : 's'} on file`}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 600,
                      bgcolor: COLORS.orangeGlass,
                      color: COLORS.orangeDark,
                      border: `1px solid ${COLORS.orange}40`
                    }}
                  />
                </Stack>
              </Box>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<Add />}
                onClick={() => setShowSubmit(true)}
                sx={pillPrimarySx}
              >
                New job
              </Button>
              <Button
                startIcon={<Refresh />}
                onClick={() => fetchJobs()}
                disabled={loading}
                sx={pillSecondarySx}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiPill
              title="Active"
              value={stats.active}
              accent={COLORS.orange}
              bg={COLORS.orangeGlass}
              icon={<HourglassTop sx={{ color: COLORS.orange, fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiPill
              title="Awaiting plan"
              value={stats.awaiting}
              accent={COLORS.indigo}
              bg={COLORS.indigoGlass}
              icon={<Assignment sx={{ color: COLORS.indigo, fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiPill
              title="In MRO / QM"
              value={stats.inMro}
              accent={COLORS.amber}
              bg={COLORS.amberGlass}
              icon={<BuildCircle sx={{ color: COLORS.amber, fontSize: 18 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiPill
              title="Completed"
              value={stats.completed}
              accent={COLORS.green}
              bg={COLORS.greenGlass}
              icon={<CheckCircleOutline sx={{ color: COLORS.green, fontSize: 18 }} />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {showSubmit && (
            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                component="form"
                onSubmit={handleSubmit}
                sx={{ ...panelShellSx, p: { xs: 2.5, md: 3 } }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>
                      Submit work order request
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                      Aircraft and work details go to Planning. The Planning Manager creates the work order
                      before Store and MRO start.
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => setShowSubmit(false)} sx={{ color: COLORS.textMuted }}>
                    Hide
                  </Button>
                </Stack>

                {submitError && <Alert severity="error" sx={{ mb: 2, ...alertErrorSx }}>{submitError}</Alert>}
                {submitSuccess && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 3, bgcolor: COLORS.greenGlass, border: `1px solid ${COLORS.green}40` }}>
                    {submitSuccess}
                  </Alert>
                )}

                <Stack spacing={1.75}>
                  <TextField
                    label="Aircraft registration"
                    required
                    fullWidth
                    value={form.registration}
                    onChange={setField('registration')}
                    sx={fieldSx}
                  />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Manufacturer"
                        fullWidth
                        value={form.manufacturer}
                        onChange={setField('manufacturer')}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Model"
                        fullWidth
                        value={form.model}
                        onChange={setField('model')}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    label="Serial number"
                    fullWidth
                    value={form.serialNumber}
                    onChange={setField('serialNumber')}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Job title"
                    required
                    fullWidth
                    value={form.title}
                    onChange={setField('title')}
                    sx={fieldSx}
                  />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Environment"
                        fullWidth
                        value={form.maintenanceType}
                        onChange={setField('maintenanceType')}
                        sx={fieldSx}
                      >
                        {HANGAR_ENVIRONMENT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Type"
                        fullWidth
                        value={form.type}
                        onChange={setField('type')}
                        sx={fieldSx}
                      >
                        {HANGAR_JOB_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="Priority"
                        fullWidth
                        value={form.priority}
                        onChange={setField('priority')}
                        sx={fieldSx}
                      >
                        <MenuItem value="LOW">Low</MenuItem>
                        <MenuItem value="MEDIUM">Medium</MenuItem>
                        <MenuItem value="HIGH">High</MenuItem>
                        <MenuItem value="CRITICAL">Critical</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Preferred date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={form.scheduledDate}
                        onChange={setField('scheduledDate')}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    minRows={3}
                    value={form.description}
                    onChange={setField('description')}
                    sx={fieldSx}
                  />
                  <TextField
                    label="Notes for hangar"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.notes}
                    onChange={setField('notes')}
                    sx={fieldSx}
                  />

                  <Box sx={{ ...surfaceInsetSx, p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.text, mb: 1 }}>
                      Attachments
                    </Typography>
                    {(form.pendingFiles || []).length > 0 && (
                      <Stack spacing={0.75} sx={{ mb: 1.25 }}>
                        {form.pendingFiles.map((file, index) => (
                          <Stack
                            key={`${file.name}-${index}`}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: COLORS.bgElevated,
                              border: `1px solid ${COLORS.glassBorder}`
                            }}
                          >
                            <Typography variant="body2" noWrap sx={{ flex: 1, color: COLORS.text }}>
                              {file.name}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<Delete />}
                              onClick={() => removePendingFile(index)}
                              sx={{ color: COLORS.red, textTransform: 'none' }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<UploadFile />}
                      disabled={submitting}
                      sx={{
                        ...pillSecondarySx,
                        borderRadius: 999
                      }}
                    >
                      Add attachment
                      <input
                        type="file"
                        hidden
                        multiple
                        onChange={(e) => {
                          addPendingFiles(e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                  </Box>

                  <Button type="submit" disabled={submitting} sx={{ ...pillPrimarySx, py: 1.1 }}>
                    {submitting ? 'Submitting…' : 'Submit request'}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} lg={showSubmit ? 7 : 12}>
            <Paper elevation={0} sx={{ ...panelShellSx, p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>
                    Requests & work orders
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    Track requests until Planning creates the work order, then open CRS, EDR, and attachments.
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  placeholder="Search jobs…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ ...fieldSx, minWidth: { sm: 220 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: COLORS.textMuted, fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>

              <Tabs
                value={historyTab}
                onChange={(_, v) => setHistoryTab(v)}
                sx={{
                  mb: 2,
                  minHeight: 40,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 40,
                    color: COLORS.textMuted
                  },
                  '& .Mui-selected': { color: `${COLORS.orange} !important` },
                  '& .MuiTabs-indicator': { bgcolor: COLORS.orange, height: 3, borderRadius: 2 }
                }}
              >
                <Tab value="active" label={`Active (${stats.active})`} />
                <Tab value="completed" label={`Completed (${stats.completed})`} />
                <Tab value="all" label={`All (${stats.total})`} />
              </Tabs>

              {listError && <Alert severity="error" sx={{ mb: 2, ...alertErrorSx }}>{listError}</Alert>}

              {loading ? (
                <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress size={36} sx={{ color: COLORS.orange }} />
                </Box>
              ) : sortedJobs.length === 0 ? (
                <Alert severity="info" sx={alertInfoSx}>
                  {jobs.length === 0
                    ? 'No jobs yet. Submit your first hangar job to get started.'
                    : 'No jobs match this filter.'}
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {sortedJobs.map((job) => {
                    const registration = typeof job.aircraftId === 'object'
                      ? job.aircraftId?.registration
                      : '—';
                    const last = job.lastAction;
                    const formBits = formBitsFor(job);
                    const meta = STAGE_META[job.customerStage] || STAGE_META.UNKNOWN;
                    return (
                      <Box
                        key={job._id}
                        onClick={() => openJobDetails(job)}
                        sx={{
                          ...glassCardSx,
                          ...glassCardHoverSx,
                          p: 2,
                          cursor: 'pointer',
                          borderLeft: `4px solid ${meta.color}`
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems={{ md: 'center' }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="subtitle1" fontWeight={800} sx={{ color: COLORS.text }}>
                                {job.mroJobNumber || job.externalJobId || 'Request'}
                              </Typography>
                              {!job.workOrderCreated && !job.mroJobNumber && (
                                <Chip
                                  size="small"
                                  label="Awaiting WO"
                                  sx={{
                                    borderRadius: 999,
                                    fontWeight: 700,
                                    bgcolor: COLORS.orangeGlass,
                                    color: COLORS.orangeDark,
                                    border: `1px solid ${COLORS.orange}40`
                                  }}
                                />
                              )}
                              <StageChip stage={job.customerStage} label={job.customerStageLabel || meta.label} />
                              {job.priority && job.priority !== 'MEDIUM' && (
                                <Chip
                                  size="small"
                                  label={job.priority}
                                  sx={{
                                    borderRadius: 999,
                                    fontWeight: 700,
                                    bgcolor: job.priority === 'CRITICAL' || job.priority === 'HIGH'
                                      ? COLORS.redGlass
                                      : COLORS.greyGlass,
                                    color: job.priority === 'CRITICAL' || job.priority === 'HIGH'
                                      ? COLORS.red
                                      : COLORS.greyDark
                                  }}
                                />
                              )}
                            </Stack>
                            <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.35 }} noWrap>
                              {job.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: COLORS.textMuted, display: 'block', mt: 0.5 }}>
                              {registration || '—'}
                              {' · '}
                              Submitted {formatShortDate(job.createdAt)}
                              {job.completedDate ? ` · Completed ${formatShortDate(job.completedDate)}` : ''}
                            </Typography>
                            {last && (
                              <Typography
                                variant="caption"
                                sx={{ color: COLORS.textSecondary, display: 'block', mt: 0.75 }}
                                noWrap
                              >
                                Latest: {last.summary}
                                {last.actorName ? ` · ${last.actorName}` : ''}
                              </Typography>
                            )}
                          </Box>
                          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                            {formBits.length > 0 && (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {formBits.map((bit) => (
                                  <Chip
                                    key={bit}
                                    size="small"
                                    icon={<Description sx={{ fontSize: '14px !important' }} />}
                                    label={bit}
                                    sx={{
                                      borderRadius: 999,
                                      bgcolor: COLORS.field,
                                      border: `1px solid ${COLORS.glassBorder}`,
                                      color: COLORS.textSecondary,
                                      fontWeight: 600
                                    }}
                                  />
                                ))}
                              </Stack>
                            )}
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openJobDetails(job);
                              }}
                              sx={{ ...pillSecondarySx, py: 0.5, px: 1.75 }}
                            >
                              Details & records
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={Boolean(detailJob)}
        onClose={() => setDetailJob(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...panelShellSx,
            bgcolor: COLORS.bgElevated
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 800 }}>
          Job details & records
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: COLORS.glassBorder }}>
          {detailJob && (
            <Stack spacing={2.25}>
              {detailError && <Alert severity="error" sx={alertErrorSx}>{detailError}</Alert>}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.text }}>
                    {detailJob.title}
                  </Typography>
                  <StageChip
                    stage={detailJob.customerStage}
                    label={detailJob.customerStageLabel || STAGE_META[detailJob.customerStage]?.label}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                  {detailJob.mroJobNumber || detailJob.externalJobId}
                  {typeof detailJob.aircraftId === 'object' && detailJob.aircraftId?.registration
                    ? ` · ${detailJob.aircraftId.registration}`
                    : ''}
                  {detailJob.completedDate
                    ? ` · Completed ${formatActionAt(detailJob.completedDate)}`
                    : ''}
                </Typography>
              </Box>

              {(detailJob.customerStage === 'CANCELLED' || detailJob.customerStage === 'REJECTED') ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>{detailJob.customerStageLabel}</Alert>
              ) : (
                <Box sx={{ ...surfaceInsetSx, p: 2 }}>
                  <Stepper
                    activeStep={stageActiveIndex(detailJob.customerStage)}
                    alternativeLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: COLORS.textSecondary, fontWeight: 600 },
                      '& .MuiStepIcon-root.Mui-active': { color: COLORS.orange },
                      '& .MuiStepIcon-root.Mui-completed': { color: COLORS.green }
                    }}
                  >
                    {STAGE_ORDER.map((stage) => (
                      <Step
                        key={stage}
                        completed={
                          stageActiveIndex(detailJob.customerStage) > STAGE_ORDER.indexOf(stage)
                          || detailJob.customerStage === 'COMPLETED'
                        }
                      >
                        <StepLabel>
                          {STAGE_META[stage]?.label || stage}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              )}

              <Divider sx={{ borderColor: COLORS.glassBorder }} />

              <Box>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: COLORS.text, mb: 1.25 }}>
                  Hangar actions
                </Typography>
                {(detailJob.customerActions || []).length === 0 ? (
                  <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                    No hangar actions recorded yet.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {[...(detailJob.customerActions || [])].reverse().map((action) => (
                      <Box
                        key={action.id}
                        sx={{
                          ...surfaceInsetSx,
                          p: 1.25,
                          borderLeft: `3px solid ${COLORS.indigo}`
                        }}
                      >
                        <Typography variant="body2" sx={{ color: COLORS.text }}>{action.summary}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                          {action.actorName} · {formatActionAt(action.at)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Divider sx={{ borderColor: COLORS.glassBorder }} />

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{ color: COLORS.text, mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Description fontSize="small" /> Forms & certificates
                </Typography>
                <Stack spacing={1}>
                  {(forms.crsDocument?.available || forms.crsPdfAvailable) ? (
                    <FormRow
                      label="CRS"
                      hint={[
                        forms.crsDocument?.originalName,
                        forms.crsDocument?.uploadedAt ? formatActionAt(forms.crsDocument.uploadedAt) : null
                      ].filter(Boolean).join(' · ')}
                    >
                      {forms.crsDocument?.available && (
                        <Button
                          size="small"
                          startIcon={<Download />}
                          disabled={formBusy === `${jobBase}/crs/file`}
                          sx={pillSecondarySx}
                          onClick={() => runFormAction(`${jobBase}/crs/file`, () =>
                            downloadAuthenticatedBinary(
                              `${jobBase}/crs/file`,
                              forms.crsDocument.originalName || 'CRS'
                            ))}
                        >
                          File
                        </Button>
                      )}
                      {forms.crsPdfAvailable && (
                        <>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            disabled={Boolean(formBusy)}
                            sx={pillSecondarySx}
                            onClick={() => openPdf(`${jobBase}/crs.pdf`)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Download />}
                            disabled={Boolean(formBusy)}
                            sx={pillSecondarySx}
                            onClick={() => downloadPdf(`${jobBase}/crs.pdf`, 'CRS.pdf')}
                          >
                            PDF
                          </Button>
                        </>
                      )}
                    </FormRow>
                  ) : (
                    <Typography variant="body2" sx={{ color: COLORS.textMuted }}>CRS not available yet.</Typography>
                  )}

                  {forms.actionTakenFormAvailable ? (
                    <FormRow
                      label="Action Taken Form"
                      hint={forms.actionTakenFormSavedAt
                        ? `Saved ${formatActionAt(forms.actionTakenFormSavedAt)}`
                        : undefined}
                    >
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        disabled={Boolean(formBusy)}
                        sx={pillSecondarySx}
                        onClick={() => openPdf(`${jobBase}/action-taken-form.pdf`)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        disabled={Boolean(formBusy)}
                        sx={pillSecondarySx}
                        onClick={() => downloadPdf(`${jobBase}/action-taken-form.pdf`, 'Action-Taken-Form.pdf')}
                      >
                        Download
                      </Button>
                    </FormRow>
                  ) : (
                    <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                      Action Taken Form not available yet.
                    </Typography>
                  )}

                  {forms.workPackagePdfAvailable ? (
                    <FormRow label="Work package report">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        disabled={Boolean(formBusy)}
                        sx={pillSecondarySx}
                        onClick={() => openPdf(`${jobBase}/work-package.pdf`)}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        disabled={Boolean(formBusy)}
                        sx={pillSecondarySx}
                        onClick={() => downloadPdf(`${jobBase}/work-package.pdf`, 'Work-Package.pdf')}
                      >
                        Download
                      </Button>
                    </FormRow>
                  ) : null}

                  {forms.maintenanceRelease ? (
                    <Box sx={{ ...surfaceInsetSx, p: 1.5 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.text }}>
                        Maintenance release
                        {forms.maintenanceRelease.certificateType
                          ? ` (${forms.maintenanceRelease.certificateType})`
                          : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                        {[
                          forms.maintenanceRelease.releasedAt
                            ? formatActionAt(forms.maintenanceRelease.releasedAt)
                            : null,
                          forms.maintenanceRelease.releaseCertificate
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  ) : null}

                  {(forms.edrs || []).length > 0 ? (
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.text, mb: 0.75 }}>
                        Engineering Defect Reports ({forms.edrs.length})
                      </Typography>
                      <Stack spacing={0.75}>
                        {forms.edrs.map((edr) => (
                          <FormRow
                            key={`${edr.edrIndex}-${edr.edrReferenceNumber}`}
                            label={edr.edrReferenceNumber || `EDR #${edr.edrIndex + 1}`}
                            hint={[
                              edr.icplNumber ? `Auth ${edr.icplNumber}` : null,
                              `Sent ${formatActionAt(edr.sentAt)}`,
                              edr.raisedBy ? `Raised by ${edr.raisedBy}` : null
                            ].filter(Boolean).join(' · ')}
                          >
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              sx={pillSecondarySx}
                              onClick={() => setEdrView(edr)}
                            >
                              View EDR
                            </Button>
                          </FormRow>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                      No sent EDRs on this job.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Divider sx={{ borderColor: COLORS.glassBorder }} />

              <Box sx={{ ...surfaceInsetSx, p: 1.5 }}>
                <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 700 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.text, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {detailJob.description || 'No description provided.'}
                </Typography>
              </Box>

              {detailJob.attachments?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: COLORS.text, mb: 1 }}>
                    Attachments
                  </Typography>
                  <Stack spacing={0.75}>
                    {detailJob.attachments.map((a) => (
                      <FormRow key={a.fileName} label={a.originalFileName || a.fileName}>
                        {a.downloadable !== false && a.fileName && (
                          <Button
                            size="small"
                            startIcon={<Download />}
                            disabled={Boolean(formBusy)}
                            sx={pillSecondarySx}
                            onClick={() => runFormAction(`att-${a.fileName}`, () =>
                              downloadAuthenticatedBinary(
                                `${jobBase}/attachments/${encodeURIComponent(a.fileName)}`,
                                a.originalFileName || a.fileName
                              ))}
                          >
                            Download
                          </Button>
                        )}
                      </FormRow>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailJob(null)} sx={pillSecondarySx}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(edrView)}
        onClose={() => setEdrView(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...panelShellSx, bgcolor: COLORS.bgElevated } }}
      >
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 800 }}>
          {edrView?.edrReferenceNumber || 'Engineering Defect Report'}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: COLORS.glassBorder }}>
          {edrView && (
            <Stack spacing={1.5}>
              <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                Sent {formatActionAt(edrView.sentAt)}
                {edrView.raisedBy ? ` · Raised by ${edrView.raisedBy}` : ''}
              </Typography>
              {EDR_VIEW_FIELDS.map(([key, label]) => {
                const value = key === 'edrReferenceNumber'
                  ? (edrView.edrReferenceNumber || edrView.form?.[key])
                  : (edrView.form?.[key] ?? edrView[key]);
                if (value == null || String(value).trim() === '') return null;
                return (
                  <Box key={key} sx={{ ...surfaceInsetSx, p: 1.25 }}>
                    <Typography variant="caption" sx={{ color: COLORS.textMuted }}>{label}</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.text, whiteSpace: 'pre-wrap' }}>
                      {String(value)}
                    </Typography>
                  </Box>
                );
              })}
              {(() => {
                const shown = new Set(EDR_VIEW_FIELDS.map(([k]) => k));
                const extras = Object.entries(edrView.form || {}).filter(([k, v]) => {
                  if (shown.has(k)) return false;
                  if (v == null || v === '') return false;
                  if (typeof v === 'object') return Object.keys(v).length > 0;
                  return String(v).trim() !== '';
                });
                if (!extras.length) return null;
                return (
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                      Additional form fields
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        mt: 0.5,
                        p: 1.25,
                        ...surfaceInsetSx,
                        fontSize: '0.75rem',
                        overflow: 'auto',
                        maxHeight: 240,
                        color: COLORS.textSecondary
                      }}
                    >
                      {JSON.stringify(Object.fromEntries(extras), null, 2)}
                    </Box>
                  </Box>
                );
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdrView(null)} sx={pillSecondarySx}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorPortal;
